import { areArraysEqual } from "../../../../app/common";
import { TalentPaymentProhibition } from "./paymentProhibitionAPI";

export const newEmptyItem = {
  id: "new",
  version: 0,
  talentId: "",
  rfReference: "",
  employer: "",
  employerName: "",
  start: "",
  enforcementAgency: "Ulosottolaitos",
  deductionType: 1,
  iban: "FI9281299710016379",
  bic: "DABAFIHH",
  type: "EXPERT_MANUAL", // Manually added items are always of type "EXPERT_MANUAL"
};

function getEndDate(date?: string): Date {
  return date ? new Date(date) : new Date("9999-12-31"); // Far future date for open-ended
}

// Ensures that the given date is within the specified start and end date range.
function dateWithinRange(
  date: string | Date,
  startDate: string | Date,
  endDate: string | Date | null, // Accept null for open-ended dates
): boolean {
  const targetDate = new Date(date);
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date("9999-12-31"); // Treat null as far future date
  return targetDate >= start && targetDate <= end;
}

// Checks if the date range from startDate to endDate spans over or covers another date range specified by rangeStart and rangeEnd.
function spansDateRange(
  startDate: string | Date,
  endDate: string | Date | null, // Accept null for open-ended dates
  rangeStart: string | Date,
  rangeEnd: string | Date | null, // Accept null for open-ended range checks
): boolean {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date("9999-12-31"); // Treat null as far future date
  const rangeStartD = new Date(rangeStart);
  const rangeEndD = rangeEnd ? new Date(rangeEnd) : new Date("9999-12-31"); // Treat null as far future date
  return start <= rangeStartD && end >= rangeEndD;
}

// Finds companies with open end dates in the list of payment prohibitions.
export function findCompaniesWithOpenEndDates(
  paymentProhibitions: TalentPaymentProhibition[],
  editedItem: TalentPaymentProhibition | undefined,
): string[] {
  return paymentProhibitions
    .filter((prohibition) => {
      if (!prohibition.employer) return false;
      const prohibitionEndDate = getEndDate(prohibition.end);
      const prohibitionStartDate = new Date(prohibition.start);

      return (
        editedItem &&
        ((editedItem.start &&
          dateWithinRange(
            editedItem.start,
            prohibitionStartDate,
            prohibitionEndDate,
          )) ||
          (editedItem.end &&
            dateWithinRange(
              editedItem.end,
              prohibitionStartDate,
              prohibitionEndDate,
            )) ||
          (editedItem.start &&
            editedItem.end &&
            spansDateRange(
              editedItem.start,
              editedItem.end,
              prohibitionStartDate,
              prohibitionEndDate,
            )))
      );
    })
    .map((prohibition) => prohibition.employer);
}

// Checks if editedItem conflicts with any other payment
// prohibition in the list that has the same employer.
function checkForConflictingDates(
  paymentProhibitions: TalentPaymentProhibition[],
  editedItem: TalentPaymentProhibition | undefined,
): boolean {
  return paymentProhibitions.some((prohibition) => {
    const prohibitionEndDate = prohibition.end ? prohibition.end : "9999-12-31"; // Handle null end date
    const prohibitionStartDate = prohibition.start;
    const editedItemStartDate = editedItem?.start || null;
    const editedItemEndDate = editedItem?.end || "9999-12-31"; // Handle null end date for open-ended range

    return (
      prohibition.id !== editedItem?.id &&
      prohibition.employer === editedItem?.employer &&
      ((editedItemStartDate &&
        dateWithinRange(
          editedItemStartDate,
          prohibitionStartDate,
          prohibitionEndDate,
        )) ||
        (editedItemEndDate &&
          dateWithinRange(
            editedItemEndDate,
            prohibitionStartDate,
            prohibitionEndDate,
          )) ||
        (editedItemStartDate &&
          editedItemEndDate &&
          spansDateRange(
            editedItemStartDate,
            editedItemEndDate,
            prohibitionStartDate,
            prohibitionEndDate,
          )) ||
        (editedItemStartDate &&
          spansDateRange(
            prohibitionStartDate,
            prohibitionEndDate,
            editedItemStartDate,
            "9999-12-31", // Handle open-ended edited items
          )))
    );
  });
}

// Helper function to check if a value is a valid numeric string
const isValidNumericString = (value: string) => {
  return /^\d*\.?\d+$/.test(value.replace(",", "."));
};

export const isSaveDisabled = (
  paymentProhibitions: TalentPaymentProhibition[],
  storedPaymentProhibitions: TalentPaymentProhibition[],
  editedItem: TalentPaymentProhibition | undefined,
) => {
  // Check if paymentProhibitions are equal to storedPaymentProhibitions
  if (areArraysEqual(paymentProhibitions, storedPaymentProhibitions)) {
    return true;
  }

  // Check if editedItem has conflicting dates with other paymentProhibitions sharing the same employer
  if (checkForConflictingDates(paymentProhibitions, editedItem)) {
    return true;
  }

  // Check for missing values in paymentProhibitions and their nested arrays
  for (let prohibition of paymentProhibitions) {
    // Check for missing values in the main fields of paymentProhibitions
    if (
      !prohibition.employer ||
      !prohibition.start ||
      !prohibition.iban ||
      !prohibition.bic ||
      !prohibition.enforcementAgency ||
      !prohibition.rfReference
    ) {
      return true;
    }

    // Check if start is after end, when both are given
    if (
      prohibition.start &&
      prohibition.end &&
      prohibition.start > prohibition.end
    ) {
      return true;
    }

    // Check in freeMonths for missing start and end
    if (
      prohibition.freeMonths &&
      prohibition.freeMonths.some((month) => !month.start || !month.end)
    ) {
      return true;
    }

    // Check in garnishmentRestrictions for missing start, end, and amount or amount is having invalid characters
    if (
      prohibition.garnishmentRestrictions &&
      prohibition.garnishmentRestrictions.some(
        (restriction) =>
          !restriction.start ||
          !restriction.end ||
          !restriction.amount ||
          !isValidNumericString(restriction.amount.toString()) ||
          restriction.start > restriction.end,
      )
    ) {
      return true;
    }

    // If deductionType is 2, deductionTypeAmount and dailyProtectedPortion are required
    if (
      prohibition.deductionType === 2 &&
      (!prohibition.deductionTypeAmount ||
        parseFloat(
          prohibition.deductionTypeAmount.toString().replace(",", "."),
        ) <= 0 ||
        !isValidNumericString(prohibition.deductionTypeAmount.toString()) ||
        !prohibition.dailyProtectedPortion ||
        parseFloat(
          prohibition.dailyProtectedPortion.toString().replace(",", "."),
        ) <= 0 ||
        !isValidNumericString(prohibition.dailyProtectedPortion.toString()))
    ) {
      return true;
    }

    // If deductionType is not 1 and deductionTypeAmount is empty, 0, not a number, or contains invalid characters, show error
    if (
      prohibition.deductionType !== 1 &&
      (!prohibition.deductionTypeAmount ||
        parseFloat(
          prohibition.deductionTypeAmount.toString().replace(",", "."),
        ) <= 0 ||
        !isValidNumericString(prohibition.deductionTypeAmount.toString()))
    ) {
      return true;
    }

    // If externalMonthlyNetIncome, dailyProtectedPortion or additionalDeduction
    // is not empty and contains invalid characters (not numeric), show error
    if (
      (prohibition.externalMonthlyNetIncome &&
        !isValidNumericString(prohibition.externalMonthlyNetIncome)) ||
      (prohibition.dailyProtectedPortion &&
        !isValidNumericString(prohibition.dailyProtectedPortion)) ||
      (prohibition.additionalDeduction &&
        !isValidNumericString(prohibition.additionalDeduction))
    ) {
      return true;
    }

    // If paymentProhibitions have items with open end date and same company, show error
    if (
      (prohibition.end === null || prohibition.end === undefined) &&
      paymentProhibitions.some(
        (item) =>
          item.employer === prohibition.employer &&
          item.end === null &&
          item.id !== prohibition.id,
      )
    ) {
      return true;
    }
  }
  // If none of the above conditions are met, do not disable save
  return false;
};
